import { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { AutocompleteInput } from "./AutocompleteInput";
import { TOUR } from "../constants/testIds";
import { ALL_COUNTRIES, ALL_FOOD } from "../utils/tours";
import {
  Search,
  Sparkles,
  Flame,
  Hotel as HotelIcon,
  Plane,
  MapPin,
  Calendar,
  Users,
  Baby,
  Wallet,
  Utensils,
  Building2,
  Loader2,
} from "lucide-react";

const TABS = [
  { id: "tours",  label: "Туры",    icon: Plane,       testId: TOUR.tabTours },
  { id: "hotels", label: "Отели",   icon: HotelIcon,   testId: TOUR.tabHotels },
  { id: "hot",    label: "Горящие", icon: Flame,       testId: TOUR.tabHot },
];

// Popular Russian departure cities for autocomplete suggestions.
// User can also freely type any city.
const DEPARTURE_CITIES = [
  "Москва", "Санкт-Петербург", "Екатеринбург", "Казань", "Новосибирск",
  "Уфа", "Самара", "Краснодар", "Ростов-на-Дону", "Челябинск",
  "Пермь", "Нижний Новгород", "Красноярск", "Тюмень", "Омск",
  "Волгоград", "Воронеж", "Иркутск", "Хабаровск", "Владивосток",
  "Калининград", "Сургут", "Мурманск", "Барнаул", "Ставрополь",
];

const todayISO = () => new Date().toISOString().slice(0, 10);

export const SearchWidget = ({ onSearch, onPickForMe, loading }) => {
  const [tab, setTab] = useState("tours");
  const [form, setForm] = useState({
    from: "Москва",
    destination: "",
    date: todayISO(),
    nights: "10",
    adults: "2",
    children: "0",
    category: "",
    budget: "",
    food: "",
    hotelName: "",
  });

  const update = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const updateInput = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSearch = () => {
    onSearch({ ...form, tab });
  };

  return (
    <div
      data-testid={TOUR.searchWidget}
      className="relative rounded-3xl bg-white shadow-[0_30px_80px_-40px_rgba(6,59,78,0.35)] border border-[#F0DFC1] overflow-hidden"
    >
      {/* Tabs */}
      <div className="flex items-center gap-1 p-2 bg-[#FFF7EC] border-b border-[#F0DFC1] overflow-x-auto">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              data-testid={t.testId}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-2xl text-sm font-semibold transition-all whitespace-nowrap ${
                active
                  ? "bg-white text-[#063B4E] shadow-md"
                  : "text-[#4A5A6B] hover:text-[#063B4E]"
              }`}
            >
              <Icon className={`w-4 h-4 ${active ? "text-coral" : ""}`} />
              {t.label}
            </button>
          );
        })}
        <div className="ml-auto hidden sm:flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-teal pr-2">
          <Sparkles className="w-3.5 h-3.5" />
          Подбор в реальном времени
        </div>
      </div>

      {/* Fields */}
      <div className="p-5 sm:p-7 grid grid-cols-1 md:grid-cols-6 gap-3">
        <FieldWrap label="Город вылета" icon={Plane} className="md:col-span-2">
          <AutocompleteInput
            id="field-from"
            testId={TOUR.fieldFrom}
            options={DEPARTURE_CITIES}
            value={form.from}
            onChange={update("from")}
            placeholder="Введите город вылета"
            className="h-11 border-[#E9D9BE] bg-white"
          />
        </FieldWrap>

        <FieldWrap label="Направление" icon={MapPin} className="md:col-span-2">
          <AutocompleteInput
            id="field-destination"
            testId={TOUR.fieldDestination}
            options={ALL_COUNTRIES}
            value={form.destination}
            onChange={update("destination")}
            placeholder="Введите направление"
            className="h-11 border-[#E9D9BE] bg-white"
          />
        </FieldWrap>

        <FieldWrap label="Дата вылета" icon={Calendar} className="md:col-span-2">
          <Input
            data-testid={TOUR.fieldDate}
            type="date"
            value={form.date}
            onChange={updateInput("date")}
            className="h-11 border-[#E9D9BE] bg-white"
          />
        </FieldWrap>

        <FieldWrap label="Ночей" icon={Calendar} className="md:col-span-1">
          <Select value={form.nights} onValueChange={update("nights")}>
            <SelectTrigger data-testid={TOUR.fieldNights} className="h-11 border-[#E9D9BE] bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[3, 5, 7, 10, 12, 14, 21].map((n) => (
                <SelectItem key={n} value={String(n)}>{n} ночей</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FieldWrap>

        <FieldWrap label="Взрослые" icon={Users} className="md:col-span-1">
          <Select value={form.adults} onValueChange={update("adults")}>
            <SelectTrigger data-testid={TOUR.fieldAdults} className="h-11 border-[#E9D9BE] bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[1, 2, 3, 4].map((n) => (
                <SelectItem key={n} value={String(n)}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FieldWrap>

        <FieldWrap label="Дети" icon={Baby} className="md:col-span-1">
          <Select value={form.children} onValueChange={update("children")}>
            <SelectTrigger data-testid={TOUR.fieldChildren} className="h-11 border-[#E9D9BE] bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[0, 1, 2, 3].map((n) => (
                <SelectItem key={n} value={String(n)}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FieldWrap>

        <FieldWrap label="Категория" icon={Sparkles} className="md:col-span-1">
          <Select value={form.category || "any"} onValueChange={(v) => update("category")(v === "any" ? "" : v)}>
            <SelectTrigger data-testid={TOUR.fieldCategory} className="h-11 border-[#E9D9BE] bg-white">
              <SelectValue placeholder="Любая" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Любая</SelectItem>
              <SelectItem value="3">3★</SelectItem>
              <SelectItem value="4">4★</SelectItem>
              <SelectItem value="5">5★</SelectItem>
            </SelectContent>
          </Select>
        </FieldWrap>

        <FieldWrap label="Питание" icon={Utensils} className="md:col-span-2">
          <Select value={form.food || "any"} onValueChange={(v) => update("food")(v === "any" ? "" : v)}>
            <SelectTrigger data-testid={TOUR.fieldFood} className="h-11 border-[#E9D9BE] bg-white">
              <SelectValue placeholder="Любое" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Любое</SelectItem>
              {ALL_FOOD.map((f) => (
                <SelectItem key={f} value={f}>{f}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FieldWrap>

        <FieldWrap label="Бюджет" icon={Wallet} className="md:col-span-2">
          <Input
            data-testid={TOUR.fieldBudget}
            value={form.budget}
            onChange={updateInput("budget")}
            placeholder="Например, 200 000 ₽"
            className="h-11 border-[#E9D9BE] bg-white"
          />
        </FieldWrap>

        <FieldWrap label="Название отеля (необязательно)" icon={Building2} className="md:col-span-2">
          <Input
            data-testid={TOUR.fieldHotelName}
            value={form.hotelName}
            onChange={updateInput("hotelName")}
            placeholder="Rixos, Titanic, Atlantis..."
            className="h-11 border-[#E9D9BE] bg-white"
          />
        </FieldWrap>
      </div>

      {/* CTA row */}
      <div className="border-t border-[#F5E7CE] px-5 sm:px-7 py-5 bg-gradient-to-r from-white via-[#FFF7EC] to-white flex flex-col sm:flex-row gap-3 sm:gap-4 sm:items-center sm:justify-between">
        <div className="text-xs text-[#4A5A6B] max-w-md">
          Мы генерируем предварительные варианты. Актуальные цены и наличие
          подтвердит менеджер после вашей заявки.
        </div>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          <Button
            data-testid={TOUR.btnPickForMe}
            onClick={onPickForMe}
            variant="outline"
            className="border-teal text-teal hover:bg-teal hover:text-white rounded-full h-12 px-6 font-semibold"
          >
            Подберите мне тур
          </Button>
          <Button
            data-testid={TOUR.btnSearch}
            onClick={handleSearch}
            disabled={loading}
            className="bg-coral hover:bg-[#d95a3f] text-white rounded-full h-12 px-8 text-base font-semibold shadow-lg shadow-coral/25"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Ищем...
              </>
            ) : (
              <>
                <Search className="w-4 h-4 mr-2" />
                Найти туры
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

const FieldWrap = ({ label, icon: Icon, children, className = "" }) => (
  <div className={className}>
    <Label className="text-[11px] uppercase tracking-[0.14em] text-[#4A5A6B] flex items-center gap-1.5 mb-1.5">
      {Icon && <Icon className="w-3.5 h-3.5 text-teal" />}
      {label}
    </Label>
    {children}
  </div>
);
